import type {
  PresignedUploadUrlInput,
  PresignedUploadUrlResponse,
} from '@erp/rpc';
import { stageUpload } from './stageUpload';
import { XLSX_MIME_TYPE } from './workbookContentType';

/**
 * Where an upload-and-start has got to. Only the transfer has a fraction;
 * `starting` is the one request that reserves the run.
 */
export type UploadStartPhase =
  | { phase: 'uploading'; fileName: string; percent: number }
  | { phase: 'starting' };

/** A staged workbook as a start procedure takes it. */
export interface StagedWorkbook {
  uploadKey: string;
  fileName: string;
}

/** Bytes done over bytes to send, across every file, as a whole percentage. */
export function overallPercent(
  sentBytes: number,
  currentBytes: number,
  currentPercent: number,
  totalBytes: number,
): number {
  if (totalBytes === 0) return 100;
  const done = sentBytes + (currentBytes * currentPercent) / 100;
  return Math.min(100, Math.round((done / totalBytes) * 100));
}

/**
 * Stage `.xlsx` workbooks in blob storage, one at a time so the bar means one
 * thing: bytes sent over bytes to send. Each goes through `getUploadUrl` and a
 * raw PUT (see `stageUpload`); the keys come back in the files' order. The
 * runs read .xlsx only and the URL is minted for that type alone, so the
 * browser's own idea of the type is not used.
 */
export async function stageWorkbooks(
  files: readonly File[],
  getUploadUrl: (
    input: PresignedUploadUrlInput,
  ) => Promise<PresignedUploadUrlResponse>,
  onPhase: (phase: UploadStartPhase) => void,
): Promise<StagedWorkbook[]> {
  const totalBytes = files.reduce((sum, file) => sum + file.size, 0);
  let sentBytes = 0;
  const staged: StagedWorkbook[] = [];
  for (const file of files) {
    const report = (percent: number) =>
      onPhase({
        phase: 'uploading',
        fileName: file.name,
        percent: overallPercent(sentBytes, file.size, percent, totalBytes),
      });
    report(0);
    const uploadKey = await stageUpload({
      file,
      contentType: XLSX_MIME_TYPE,
      getUploadUrl,
      onUploadProgress: report,
    });
    sentBytes += file.size;
    staged.push({ uploadKey, fileName: file.name });
  }
  return staged;
}

/** The staged file at `index`; throws when staging returned fewer than were sent. */
export function stagedAt<T>(staged: readonly T[], index: number): T {
  const file = staged[index];
  if (file === undefined) throw new Error('A staged file is missing.');
  return file;
}
